/**
 * The studio's shared settings controls.
 *
 * One row per question: what it is on the left, the control on the right, and a
 * sentence underneath saying what the current answer MEANS. That sentence is
 * what lets the controls stay small — the surfaces these replaced (the new-deck
 * modal, the Edit tab) spelled the meaning out inside the option labels, which
 * is why both needed a wrapping field of pills to say anything.
 *
 * Pick by the shape of the answer, not by how it looks:
 *   - yes or no                → <Toggle>
 *   - two or three exclusives  → <Segmented>
 *   - more than five           → <Segmented wrap> (stacks under the label)
 * Anything longer than that wants a <select>, which needs no wrapper here.
 *
 * Styles live in `styles/base.css` (`.opts`, `.opt-row`, `.seg`, `.switch`) —
 * shared, not per-surface, so the modal and the panel can't drift apart.
 */

import type { ReactNode } from 'react';

type OptionRowProps = {
  label: ReactNode;
  hint?: ReactNode;
  /** A caveat that gets its own full-width line under the row, rather than
   *  being squeezed in beside the control where it reads as a second one. */
  note?: ReactNode;
  /** The REASONING behind the answer — why the default is the default, what a
   *  measurement showed, which rungs a model has. Folded away behind "Why this
   *  matters" so the row is one line by default: a hint says what the current
   *  answer means, and everything past that sentence is reference material
   *  people read once. Native <details>, so it needs no state and Find-in-page
   *  still reaches it. */
  more?: ReactNode;
  /** Steps this row in under the one above and hangs it off a short rule, so
   *  a dependency is visible rather than implied by the wording. */
  sub?: boolean;
  /** Drops the control below the label — for one too wide to sit beside it. */
  stack?: boolean;
  children?: ReactNode;
};

/** What a segment can be worth. Every current caller uses a string or a
 *  boolean; the generic below keeps `onChange` narrowed to whichever it is,
 *  so a handler can't be handed a value the options never offered. */
export type SegmentValue = string | number | boolean;

export type SegmentOption<T extends SegmentValue> = {
  value: T;
  label: ReactNode;
  title?: string;
  /** Per-option disable: an answer that exists on this install but cannot be
   *  given right now stays VISIBLE, with `title` carrying the reason. Hiding it
   *  instead would read as the option not existing — see the metered-mode
   *  segment in canvas/EngineAccount.tsx, disabled until a key is set. */
  disabled?: boolean;
};

type SegmentedProps<T extends SegmentValue> = {
  /** Nullable: "no segment lit" is a real state, not a missing value — a
   *  layout that matches no preset, or repeated elements whose current values
   *  disagree. The comparison below simply finds no match. */
  value: T | null;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
  disabled?: boolean;
  /** Lets the segments run onto a second line and stretches the track — use
   *  with `stack` on the row. */
  wrap?: boolean;
  label?: string;
};

type ToggleProps = {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
  disabled?: boolean;
};

/** A card of option rows, hairline-separated. */
export function OptionRows({ children }: { children?: ReactNode }) {
  return <div className="opts">{children}</div>;
}

/**
 * One row of the card.
 * `sub` steps a row in under the one above it (the video clip under
 * Photography) and hangs it off a short rule, so the dependency is visible
 * rather than implied by the wording.
 * `stack` drops the control below the label — for a control too wide to sit
 * beside it (a long segmented ladder, a swatch row in a narrow panel).
 * `note` is a sentence ABOUT the answer just given (a warning, a caveat) and
 * gets its own full-width line under the row. It is not a second control —
 * passing prose as an extra child of the control put a paragraph's worth of
 * intrinsic width in a `flex: none` box and crushed the label column to one
 * word per line.
 */
export function OptionRow({ label, hint, note, more, sub = false, stack = false, children }: OptionRowProps) {
  return (
    <div className={'opt-row' + (sub ? ' opt-row--sub' : '') + (stack ? ' opt-row--stack' : '')}>
      <div className="opt-row__text">
        <span className="opt-row__label">{label}</span>
        {hint ? <span className="opt-row__hint">{hint}</span> : null}
        {more ? (
          <details className="opt-more">
            <summary className="opt-more__toggle">Why this matters</summary>
            <div className="opt-more__body">{more}</div>
          </details>
        ) : null}
      </div>
      <div className="opt-row__control">{children}</div>
      {note ? <div className="opt-row__note">{note}</div> : null}
    </div>
  );
}

/**
 * Exclusive choice. Replaces a row of chips: chips could show which option was
 * ON but never that the others were the alternatives to it.
 *
 * `options` are `{ value, label, title? }`. `wrap` lets the segments run onto a
 * second line and stretches the track — use it with `stack` on the row.
 */
export function Segmented<T extends SegmentValue>({
  value,
  onChange,
  options,
  disabled = false,
  wrap = false,
  label,
}: SegmentedProps<T>) {
  return (
    <div className={'seg' + (wrap ? ' seg--wrap' : '')} role="group" aria-label={label || undefined}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className={'seg__btn' + (value === o.value ? ' is-active' : '')}
          title={o.title || undefined}
          aria-pressed={value === o.value}
          // Per-option disable: an answer that exists on this install but can't
          // be given right now stays visible, with `title` carrying the reason.
          disabled={disabled || !!o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** On or off. A pair of chips costs two reads to answer one yes-or-no. */
export function Toggle({ checked, onChange, label, disabled = false }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className="switch"
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="switch__dot" />
    </button>
  );
}
